<script setup lang="ts">
import type { RouteLocationNormalized, RouteMeta } from '#vue-router';

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.app;

const route = useRoute();
const routeBaseName = useRouteBaseName();
const head = useLocaleHead();
const { t, locale } = useI18n();
const { initBeacon, switchBeacon } = useHelpscoutBeacon();
const partnerId = usePartnerId();
const gtmStore = useGtmStore();

const gtmContainerID = config?.gtmContainerID;

if (gtmContainerID) {
  const { proxy } = useScriptGoogleTagManager({
    id: gtmContainerID,
    scriptOptions: { warmupStrategy: 'preconnect' },
    onBeforeGtmStart: (gtag) => {
      gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'granted',
        wait_for_update: 500,
      });
    },
  });
  // Registered once, here: a second useScript call would attach a handler to the calling component's scope.
  gtmStore.connect(proxy.dataLayer);
}

// Declared before the computeds: useSeoMeta evaluates them during setup, a later declaration is a TDZ error.
const pageTitle = (meta: RouteMeta) => (meta.title ? t(meta.title) : (config?.title ?? t('app.title')));
const pageDescription = (meta: RouteMeta) => t(meta.description ?? 'app.seo.description');

const title = computed(() => pageTitle(route.meta));
const description = computed(() => pageDescription(route.meta));

useSeoMeta({
  title,
  description,
  ogDescription: description,
});

watch(locale, switchBeacon);

let trackedPath: string | undefined;
const syncPageContext = (to: RouteLocationNormalized) => {
  gtmStore.setPageContext({ routeBaseName: routeBaseName(to), language: locale.value, partnerId: partnerId.value });
};
const trackPageView = async (path: string) => {
  if (path === trackedPath) {
    return;
  }
  trackedPath = path;
  // unhead applies the title after the route resolves, so it is only correct a tick later.
  await nextTick();
  gtmStore.trackPageView(globalThis.location.href, globalThis.document.title);
};

syncPageContext(route);
const stopPageTracking = useRouter().afterEach((to) => {
  syncPageContext(to);
  void trackPageView(to.path);
  if (!globalThis.Beacon?.('info')) return;
  globalThis.Beacon('event', { type: 'page-viewed', url: to.fullPath, title: pageTitle(to.meta) });
});

onMounted(() => {
  void initBeacon();
  void trackPageView(route.path);
});
onUnmounted(stopPageTracking);
</script>

<template>
  <Html
    :lang="head.htmlAttrs?.lang"
    :dir="head.htmlAttrs?.dir"
    class="motion-safe:scroll-smooth"
  >
    <Head>
      <template
        v-for="link in head.link"
        :key="link.id"
      >
        <Link
          :id="link.id"
          :rel="link.rel"
          :href="link.href"
          :hreflang="link.hreflang"
          :nonce="link.nonce"
        />
      </template>
      <template
        v-for="meta in head.meta"
        :key="meta.id"
      >
        <Meta
          :id="meta.id"
          :property="meta.property"
          :content="meta.content"
        />
      </template>
    </Head>
    <NuxtLoadingIndicator />
    <NuxtRouteAnnouncer />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <UiToaster />
  </Html>
</template>
